/**
 * DateTimeField — single Operator-styled date/time picker for all date-typed fields.
 *
 * Modes (from attr_type + Designer format):
 *   date      → calendar only
 *   datetime  → calendar + time
 *   time      → time only
 *
 * Uses MUI X pickers + theme tokens (no native browser date UI).
 */
import React, { useMemo, useRef } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import {
  applyPickerDate,
  formatDateToStored,
  parseStoredToDate,
  resolveDateTimeFieldMode,
  type DateTimeFieldMode,
} from './fieldInputTypes'

export interface DateTimeFieldProps {
  /** Attribute type: date | datetime | time (or string containing those). */
  attrType?: string | null
  /** Designer format: date | datetime | time — overrides presentation within date attrs. */
  format?: string | null
  /** Explicit mode — wins over attrType/format when set. */
  mode?: DateTimeFieldMode
  value: unknown
  onChange: (next: string | null) => void
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
  autoFocus?: boolean
  /** Compact table-cell chrome (standard underline). */
  variant?: 'outlined' | 'standard'
  size?: 'small' | 'medium'
  fullWidth?: boolean
  label?: string
  placeholder?: string
  error?: boolean
  helperText?: string
  'aria-label'?: string
}

function DateTimeFieldInner({
  mode,
  value,
  onChange,
  disabled = false,
  required = false,
  readOnly = false,
  autoFocus = false,
  variant = 'outlined',
  size = 'small',
  fullWidth = true,
  label,
  placeholder,
  error,
  helperText,
  'aria-label': ariaLabel,
}: DateTimeFieldProps & { mode: DateTimeFieldMode }) {
  const parsed = useMemo(() => parseStoredToDate(value, mode), [value, mode])
  const pickerOpenRef = useRef(false)

  const textFieldProps = {
    size,
    fullWidth,
    variant,
    required,
    error,
    helperText,
    placeholder,
    label,
    autoFocus,
    InputProps: { readOnly },
    inputProps: {
      'aria-label': ariaLabel ?? label ?? (mode === 'time' ? 'Time' : mode === 'datetime' ? 'Date and time' : 'Date'),
    },
  } as const

  const common = {
    value: parsed,
    onChange: (d: Date | null) =>
      onChange(formatDateToStored(applyPickerDate(d, mode, pickerOpenRef.current), mode)),
    onOpen: () => {
      pickerOpenRef.current = true
    },
    onClose: () => {
      pickerOpenRef.current = false
    },
    disabled: disabled || readOnly,
    slotProps: {
      textField: textFieldProps,
      openPickerButton: {
        'aria-label':
          mode === 'time' ? 'Choose time' : mode === 'datetime' ? 'Choose date and time' : 'Choose date',
      },
      // One-click "now" (owner request): 'today' sets the current date for
      // DatePicker and the current date+time for DateTimePicker/TimePicker
      // (MUI X getTodayDate — date mode → startOfDay(now), else → now), then
      // commits and closes — no separate Accept click needed.
      actionBar: { actions: ['today'] as Array<'today'> },
    },
  }

  if (mode === 'time') {
    return <TimePicker {...common} ampm={false} views={['hours', 'minutes']} />
  }
  if (mode === 'datetime') {
    // Calendar only — picking a day stamps wall-clock now and closes.
    // Time is edited in the input, not in a second clock step.
    return (
      <DateTimePicker
        {...common}
        ampm={false}
        views={['year', 'month', 'day']}
        openTo="day"
        closeOnSelect
        // views without hours make MUI drop time from the derived format —
        // keep time visible/editable in the field.
        format="yyyy-MM-dd HH:mm:ss"
      />
    )
  }
  return <DatePicker {...common} />
}

export function DateTimeField(props: DateTimeFieldProps) {
  const mode =
    props.mode ?? resolveDateTimeFieldMode(props.attrType, props.format) ?? null
  if (!mode) return null

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimeFieldInner {...props} mode={mode} />
    </LocalizationProvider>
  )
}
